import React, { useRef, useState } from 'react';

/**
 * Card "WhatsApp" para la página de contacto de Decototal.
 * Clic en la card -> se despliega mini-form (nombre, correo, teléfono)
 * -> "Abrir WhatsApp" -> lead a HubSpot + WhatsApp con mensaje personalizado.
 * El agente ubica el lead en HubSpot por el número de teléfono.
 *
 * Reutiliza las clases del sitio (card card-style8, media, íconos ti-*)
 * para verse igual que las cards de Ubicación y Teléfono.
 */

// Nombres internos de los campos del form de HubSpot de WhatsApp
// (firstname, phone y email son obligatorios en ese form; lastname es opcional).
const CAMPOS = {
  nombre: 'firstname',
  correo: 'email',
  telefono: 'phone',
};

// Campos adicionales con valor fijo que el mini-form no pide al usuario.
// El form de WhatsApp no exige ninguno; se deja la lista por si se agregan campos ocultos.
const CAMPOS_EXTRA = [];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// Deja solo 10 dígitos (acepta +52, 52 o 521 al inicio)
function normalizarTelefono(valor) {
  let d = valor.replace(/\D/g, '');
  if (d.length === 13 && d.startsWith('521')) d = d.slice(3);
  else if (d.length === 12 && d.startsWith('52')) d = d.slice(2);
  return d;
}

function leerHutk() {
  const m = document.cookie.match(/(?:^|;\s*)hubspotutk=([^;]+)/);
  return m ? m[1] : undefined;
}

async function enviarAHubSpot({ portalId, formId, nombre, correo, telefono }) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 4000);

  const fields = [
    { name: CAMPOS.nombre, value: nombre },
    { name: CAMPOS.correo, value: correo },
    { name: CAMPOS.telefono, value: telefono },
  ];
  CAMPOS_EXTRA.forEach((c) => fields.push(c));

  try {
    const res = await fetch(
      `https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${formId}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
        signal: controller.signal,
        body: JSON.stringify({
          fields,
          context: { hutk: leerHutk(), pageUri: window.location.href, pageName: document.title },
        }),
      }
    );
    if (res.ok) {
      console.info('[WhatsAppContactoCard] HubSpot aceptó el envío', fields);
    } else {
      const detalle = await res.text();
      console.warn('[WhatsAppContactoCard] HubSpot rechazó el envío:', res.status, detalle, fields);
    }
    return res.ok;
  } catch (e) {
    console.warn('[WhatsAppContactoCard] Error de red con HubSpot:', e);
    return false;
  } finally {
    clearTimeout(timer);
  }
}

const WhatsAppContactoCard = ({
  telefonoWhatsApp,
  portalId,
  formId,
  avisoUrl = '/aviso-de-privacidad',
}) => {
  const [abierto, setAbierto] = useState(false);
  const [nombre, setNombre] = useState('');
  const [correo, setCorreo] = useState('');
  const [telefono, setTelefono] = useState('');
  const [trampa, setTrampa] = useState(''); // honeypot anti-bots
  const [errores, setErrores] = useState({});
  const [enviando, setEnviando] = useState(false);
  const nombreRef = useRef(null);
  const correoRef = useRef(null);
  const telefonoRef = useRef(null);

  const alternar = () => {
    const siguiente = !abierto;
    setAbierto(siguiente);
    if (siguiente) setTimeout(() => nombreRef.current && nombreRef.current.focus(), 320);
  };

  const alTeclear = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      alternar();
    }
  };

  const enviar = async (ev) => {
    ev.preventDefault();
    if (enviando || trampa) return;

    const tel = normalizarTelefono(telefono);
    const e = {};
    if (nombre.trim().length < 2) e.nombre = 'Escribe tu nombre.';
    if (!EMAIL_RE.test(correo.trim())) e.correo = 'Escribe un correo válido, por ejemplo nombre@correo.com.';
    if (tel.length !== 10) e.telefono = 'Escribe tu número a 10 dígitos, por ejemplo 55 1234 5678.';
    setErrores(e);
    if (e.nombre) return nombreRef.current.focus();
    if (e.correo) return correoRef.current.focus();
    if (e.telefono) return telefonoRef.current.focus();

    setEnviando(true);

    // Se abre la pestaña dentro del clic para que Safari/iOS no la bloquee
    const ventana = window.open('', '_blank');

    const primerNombre = nombre.trim().split(/\s+/)[0];
    const mensaje = `Hola, soy ${primerNombre}. Me gustaría recibir información sobre los productos de Decototal.`;
    const url = `https://wa.me/${telefonoWhatsApp}?text=${encodeURIComponent(mensaje)}`;

    // Si HubSpot falla o tarda (máx. 4 s), WhatsApp se abre igual
    await enviarAHubSpot({ portalId, formId, nombre: nombre.trim(), correo: correo.trim(), telefono: tel });

    if (ventana) ventana.location.href = url;
    else window.location.href = url;

    setEnviando(false);
  };

  const campo = (hayError) => ({
    fontSize: 16, // 16px evita el zoom automático en iOS
    background: '#f4f6f8',
    borderColor: hayError ? '#c62828' : undefined,
  });
  const msgError = { color: '#c62828', fontSize: 13, marginTop: 4 };

  return (
    <div className="card card-style8 mt-1-9 wow fadeIn" data-wow-delay="500ms">
      <style>{`
        .dt-wa-header { cursor: pointer; }
        .dt-wa-header:focus-visible, .dt-wa-enviar:focus-visible { outline: 3px solid #007bff; outline-offset: 2px; }
        .dt-wa-panel { display: grid; transition: grid-template-rows .3s ease, visibility .3s; }
        .dt-wa-chevron { transition: transform .3s ease; }
        @media (prefers-reduced-motion: reduce) {
          .dt-wa-panel, .dt-wa-chevron { transition: none !important; }
        }
      `}</style>

      <div
        className="card-body p-1-9 dt-wa-header"
        role="button"
        tabIndex={0}
        aria-expanded={abierto}
        aria-controls="dt-wa-panel"
        onClick={alternar}
        onKeyDown={alTeclear}
      >
        <div className="media">
          <div><i className="ti-comment-alt text-white"></i></div>
          <div className="media-body ms-3">
            <h4 className="h5">WhatsApp</h4>
            <span className="d-block">Escríbenos y un asesor te responde</span>
          </div>
          <i
            className="ti-angle-down dt-wa-chevron align-self-center"
            aria-hidden="true"
            style={{ transform: abierto ? 'rotate(180deg)' : 'none' }}
          ></i>
        </div>
      </div>

      <div
        id="dt-wa-panel"
        className="dt-wa-panel"
        style={{ gridTemplateRows: abierto ? '1fr' : '0fr', visibility: abierto ? 'visible' : 'hidden' }}
      >
        <div style={{ overflow: 'hidden', minHeight: 0 }}>
          <form onSubmit={enviar} noValidate className="px-4 pb-4 px-md-5 pb-md-5">
            <div className="mb-3">
              <label htmlFor="dt-wa-nombre" className="form-label">Nombre</label>
              <input
                id="dt-wa-nombre"
                ref={nombreRef}
                className="form-control"
                type="text"
                autoComplete="given-name"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                aria-invalid={!!errores.nombre}
                aria-describedby={errores.nombre ? 'dt-wa-err-nombre' : undefined}
                style={campo(errores.nombre)}
              />
              {errores.nombre && <div id="dt-wa-err-nombre" role="alert" style={msgError}>{errores.nombre}</div>}
            </div>

            <div className="mb-3">
              <label htmlFor="dt-wa-correo" className="form-label">Correo</label>
              <input
                id="dt-wa-correo"
                ref={correoRef}
                className="form-control"
                type="email"
                inputMode="email"
                autoComplete="email"
                value={correo}
                onChange={(e) => setCorreo(e.target.value)}
                aria-invalid={!!errores.correo}
                aria-describedby={errores.correo ? 'dt-wa-err-correo' : undefined}
                style={campo(errores.correo)}
              />
              {errores.correo && <div id="dt-wa-err-correo" role="alert" style={msgError}>{errores.correo}</div>}
            </div>

            <div className="mb-3">
              <label htmlFor="dt-wa-telefono" className="form-label">Tu número de WhatsApp</label>
              <input
                id="dt-wa-telefono"
                ref={telefonoRef}
                className="form-control"
                type="tel"
                inputMode="tel"
                autoComplete="tel-national"
                placeholder="55 1234 5678"
                value={telefono}
                onChange={(e) => setTelefono(e.target.value)}
                aria-invalid={!!errores.telefono}
                aria-describedby={errores.telefono ? 'dt-wa-err-telefono' : 'dt-wa-ayuda-telefono'}
                style={campo(errores.telefono)}
              />
              <div id="dt-wa-ayuda-telefono" className="form-text">
                Usa el número desde el que nos escribirás.
              </div>
              {errores.telefono && <div id="dt-wa-err-telefono" role="alert" style={msgError}>{errores.telefono}</div>}
            </div>

            {/* Honeypot: invisible para personas, los bots lo llenan */}
            <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px' }}>
              <label>
                No llenar
                <input type="text" tabIndex={-1} autoComplete="off" value={trampa} onChange={(e) => setTrampa(e.target.value)} />
              </label>
            </div>

            <button
              type="submit"
              className="dt-wa-enviar"
              disabled={enviando}
              style={{
                width: '100%',
                padding: '14px 20px',
                fontSize: 17,
                fontWeight: 700,
                color: '#fff',
                background: '#148a48', // contraste AA con texto blanco
                border: 0,
                borderRadius: 4,
                cursor: enviando ? 'wait' : 'pointer',
                opacity: enviando ? 0.75 : 1,
              }}
            >
              {enviando ? 'Abriendo WhatsApp…' : 'Abrir WhatsApp'}
            </button>

            <p className="form-text mt-3 mb-0">
              Al enviar este formulario, aceptas nuestro{' '}
              <a href={avisoUrl} target="_blank" rel="noopener noreferrer">Aviso de Privacidad</a>.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppContactoCard;